import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {useLaraIframe} from '~/routes/translate/helpers/useLaraIframe'
import {TranslateSkeleton} from '~/routes/translate/TranslateSkeleton'
import {ActionFunctionArgs, LinksFunction, MetaFunction, useLoaderData} from 'react-router'
import {enrichedMeta} from '~/utils/meta.server'
import {localizedPaths} from '~/utils/routes'
import {parseLangPair, supportedLanguages} from '~/utils/locales'
import {getPathSegment} from '~/utils/misc'
import {useRootLoaderData} from '~/utils/useRootLoaderData'
import {getAppSearchParams, getWebsiteSearchParams} from '~/utils/url'
import {checkAuth} from '~/utils/auth'
import {overlay} from '~/ui/overlay'
import {tAppRedirect} from '~/utils/redirect.server'
import {isbot} from 'isbot'

export const links: LinksFunction = () => {
  return [
    ...supportedLanguages.map(language => ({
      rel: 'alternate',
      hrefLang: language,
      href: 'https://laratranslate.com' + localizedPaths['/translate'][language]
    })),
    {rel: 'preconnect', href: 'https://app.laratranslate.com'},
    {rel: 'dns-prefetch', href: 'https://app.laratranslate.com'}
  ]
}

export const meta: MetaFunction<typeof loader> = ({data}) => {
  if (!data?.meta) return []

  return data.meta
}

export const loader = async ({request}: ActionFunctionArgs) => {
  const lastPathSegment = getPathSegment(request.url, 'last')
  const langPair = parseLangPair(lastPathSegment)
  const url = new URL(request.url)
  const {search} = url

  const isAuthenticated = checkAuth(request)
  const {noRedirect: noRedirectParam} = getWebsiteSearchParams(search)
  const appSearchParams = getAppSearchParams(search)

  if (isAuthenticated && !noRedirectParam) {
    return tAppRedirect('/translate', request, {preserveSearch: true})
  }

  return {
    meta: await enrichedMeta({
      with: ['og', 'twitter'],
      request,
      namespace: 'translate',
      langPair
    }),
    langPair,
    appSearchParams,
    isBot: isbot(request.headers.get('user-agent'))
  }
}

export default function Translate() {
  const {shouldDisplayFooter} = useRootLoaderData()
  const {langPair, appSearchParams, isBot} = useLoaderData<typeof loader>()

  const {t} = useTranslation('translate')

  const {iframeRefCallback, isLoadingIframe, everythingSettled, isDialogOpen} = useLaraIframe({
    appSearchParams,
    isBot
  })

  const showLanguagesSkeleton = Boolean(
    langPair || appSearchParams.source || appSearchParams.target
  )
  const shouldRenderSkeleton = !everythingSettled

  return (
    <main className="flex w-full grow flex-col items-center text-neutral-1000">
      {isDialogOpen && overlay}
      <section className="flex min-h-[40.5rem] w-full flex-1 flex-col rounded-lg text-neutral-1000 transition-colors">
        <section
          className={clsx(
            'flex flex-1 flex-col lg:px-2',
            shouldDisplayFooter ? 'min-h-[81vh] lg:pb-[4.5rem]' : 'lg:pb-2'
          )}
        >
          <div
            className={clsx(
              'relative flex size-full grow flex-col overflow-x-hidden rounded-t-lg text-neutral-1000 transition-colors lg:rounded-lg',
              isLoadingIframe && 'bg-neutral-25'
            )}
          >
            {shouldRenderSkeleton && (
              <TranslateSkeleton showLanguagesSkeleton={showLanguagesSkeleton} />
            )}
            <iframe
              id="translateComponentIframe"
              key="translateIframe"
              title={t('title')}
              ref={iframeRefCallback}
              src="about:blank"
              allow="microphone; clipboard-read; clipboard-write"
              className={clsx(
                'z-10 size-full grow rounded-t-lg border-none transition-height duration-100 lg:rounded-lg',
                isLoadingIframe && 'hidden'
              )}
            />
          </div>
        </section>
      </section>
    </main>
  )
}
